// /mis-bonos — listado de bonos del usuario logado.
//
// Muestra: bonos vigentes (saldo + caducidad), bonos pasados (caducados o
// agotados), historial reciente de movimientos. Sin sesión → redirige a
// /horarios. Misma rehidratación asíncrona que MisReservas.

import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Ticket, AlertCircle, Check, RotateCcw, X, Plus, Clock } from 'lucide-react';

import { useAuth, readToken } from '../context/AuthContext';
import CodigoDescuento from '../components/CodigoDescuento';
import { getMisBonos, getBonosTipos, crearCheckoutBono, getProfesorPrecios, ApiError } from '../lib/api';

function localePara(lang) {
  const map = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  return map[lang] || 'es-ES';
}

function fmtFecha(iso, lang) {
  if (!iso) return '';
  const d = new Date(iso);
  return new Intl.DateTimeFormat(localePara(lang), {
    day: 'numeric', month: 'long', year: 'numeric',
  }).format(d);
}

function diasHasta(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const hoy = new Date();
  return Math.round((d - hoy) / 86400000);
}

function CardBono({ bono, lang }) {
  const { t } = useTranslation();
  const dias = diasHasta(bono.fecha_caducidad);
  const proximaCaducidad = dias !== null && dias >= 0 && dias <= 30;
  const estadoLabel = bono.caducado
    ? t('auth.mis_bonos.estado.caducado')
    : bono.estado === 'cancelado'
      ? t('auth.mis_bonos.estado.cancelado')
      : !bono.es_membresia && bono.creditos_restantes <= 0
        ? t('auth.mis_bonos.estado.agotado')
        : t('auth.mis_bonos.estado.activo');

  return (
    <article className={'mb-card' + (bono.vigente ? ' mb-card--activo' : ' mb-card--pasado')}>
      <header className="mb-card-head">
        <Ticket size={18} strokeWidth={1.5} />
        <h3 className="mb-card-nombre">{bono.nombre}</h3>
        <span className={'mb-card-estado mb-card-estado--' + (bono.vigente ? 'activo' : 'pasado')}>
          {estadoLabel}
        </span>
      </header>

      <div className="mb-card-body">
        {bono.es_membresia ? (
          <div className="mb-card-creditos mb-card-creditos--ilimitado">
            {t('auth.mis_bonos.ilimitado')}
          </div>
        ) : (
          <div className="mb-card-creditos">
            <span className="mb-card-creditos-restante">{bono.creditos_restantes}</span>
            <span className="mb-card-creditos-sep">/</span>
            <span className="mb-card-creditos-total">{bono.creditos_iniciales}</span>
            <span className="mb-card-creditos-label">{t('auth.mis_bonos.creditos')}</span>
          </div>
        )}

        <dl className="mb-card-detalles">
          <div className="mb-card-detalle">
            <dt>{t('auth.mis_bonos.compra')}</dt>
            <dd>{fmtFecha(bono.fecha_compra, lang)}</dd>
          </div>
          {bono.fecha_caducidad && (
            <div className={'mb-card-detalle' + (proximaCaducidad ? ' mb-card-detalle--warning' : '')}>
              <dt>{t('auth.mis_bonos.caducidad')}</dt>
              <dd>
                {fmtFecha(bono.fecha_caducidad, lang)}
                {proximaCaducidad && !bono.caducado && (
                  <> · <span className="mb-card-warning"><AlertCircle size={12} strokeWidth={2} /> {t('auth.mis_bonos.caduca_pronto', { dias })}</span></>
                )}
              </dd>
            </div>
          )}
          {!bono.fecha_caducidad && (
            <div className="mb-card-detalle">
              <dt>{t('auth.mis_bonos.caducidad')}</dt>
              <dd>{t('auth.mis_bonos.sin_caducidad')}</dd>
            </div>
          )}
        </dl>
      </div>
    </article>
  );
}

const ICONO_ACCION = {
  descuento: <Check size={14} strokeWidth={1.5} />,
  restitucion_cancelacion_con_antelacion: <RotateCcw size={14} strokeWidth={1.5} />,
  perdida_cancelacion_tardia: <X size={14} strokeWidth={1.5} />,
  perdida_no_show: <X size={14} strokeWidth={1.5} />,
  confirmacion_asistencia: <Check size={14} strokeWidth={1.5} />,
};

function ItemHistorial({ item, lang }) {
  const { t } = useTranslation();
  const fecha = new Intl.DateTimeFormat(localePara(lang), {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(new Date(item.created_at));
  const sign = item.delta > 0 ? '+' : item.delta < 0 ? '' : '·';
  return (
    <li className={'mb-hist-item mb-hist-item--' + item.accion}>
      <span className="mb-hist-icon">{ICONO_ACCION[item.accion] || <Ticket size={14} strokeWidth={1.5} />}</span>
      <div className="mb-hist-info">
        <span className="mb-hist-accion">{t('auth.mis_bonos.acciones.' + item.accion, { defaultValue: item.accion })}</span>
        <span className="mb-hist-fecha">{fecha}</span>
      </div>
      <span className={'mb-hist-delta mb-hist-delta--' + (item.delta > 0 ? 'pos' : item.delta < 0 ? 'neg' : 'zero')}>
        {sign}{item.delta !== 0 ? item.delta : ''}
      </span>
    </li>
  );
}

function formatEur(n, lang) {
  return new Intl.NumberFormat(localePara(lang), { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
}

function ModalComprarBono({ onClose, lang, descuentosBono }) {
  const { t } = useTranslation();
  const [tipos, setTipos] = useState({ data: null, error: null, loading: true });
  const [enviando, setEnviando] = useState(null);  // bono_tipo_id en flight
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    getBonosTipos(lang)
      .then((res) => { if (!cancelled) setTipos({ data: res.data || [], error: null, loading: false }); })
      .catch((err) => { if (!cancelled) setTipos({ data: null, error: err.message || 'error', loading: false }); });
    return () => { cancelled = true; };
  }, [lang]);

  const [codigoSel, setCodigoSel] = useState(null);
  const comprar = async (tipo) => {
    setErrorMsg(null);
    setEnviando(tipo.id);
    try {
      const token = readToken();
      const session = await crearCheckoutBono(token, tipo.id, codigoSel?.codigo);
      if (session?.gratuito) {
        window.location.href = '/bonos/success';
        return;
      }
      if (session?.url) {
        window.location.href = session.url;
        return;
      }
      throw new Error('Sin URL de Stripe');
    } catch (err) {
      setEnviando(null);
      if (err instanceof ApiError && err.code) {
        setErrorMsg(t('comprar_bono.errores.' + err.code, { defaultValue: err.message || t('comprar_bono.errores.generico') }));
      } else {
        setErrorMsg(t('comprar_bono.errores.generico'));
      }
    }
  };

  // Solo bonos comprables: tipo 'bono' o 'membresia*' con precio > 0.
  // Ocultamos 'clase_suelta' porque ya se compra al reservar online.
  const comprables = (tipos.data || []).filter((b) =>
    (b.tipo === 'bono' || (b.tipo || '').startsWith('membresia')) && Number(b.precio_euros) > 0
  );

  return createPortal(
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal modal-comprar-bono" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onClose} aria-label={t('comun.cerrar')}>
          <X size={20} strokeWidth={1.5} />
        </button>
        <h2 className="modal-titulo">{t('comprar_bono.titulo')}</h2>
        <p className="modal-sub">{t('comprar_bono.sub')}</p>

        {tipos.loading && (
          <div className="modal-cargando"><Loader2 size={20} className="spin" strokeWidth={1.5} /></div>
        )}
        {tipos.error && (
          <div className="modal-error-general">
            <AlertCircle size={16} strokeWidth={1.75} />
            <span>{t('comprar_bono.error_carga')}</span>
          </div>
        )}

        {!tipos.loading && comprables.length > 0 && (
          <CodigoDescuento ambito="bonos" preview={false} onChange={setCodigoSel} disabled={!!enviando} />
        )}

        {!tipos.loading && comprables.length > 0 && (
          <ul className="cb-lista">
            {comprables.map((b) => {
              const esMembresia = (b.tipo || '').startsWith('membresia');
              const inFlight = enviando === b.id;
              return (
                <li key={b.id} className="cb-item">
                  <div className="cb-item-info">
                    <span className="cb-item-nombre">{b.nombre}</span>
                    {b.descripcion && <span className="cb-item-desc">{b.descripcion}</span>}
                    <span className="cb-item-meta">
                      {esMembresia
                        ? t('comprar_bono.meta_membresia', { dias: b.validez_dias || '?' })
                        : t('comprar_bono.meta_bono', {
                            creditos: b.creditos_incluidos || '?',
                            dias: b.validez_dias || '?',
                          })}
                    </span>
                  </div>
                  <div className="cb-item-cta">
                    {(() => {
                      const desc = descuentosBono?.get(b.id);
                      if (desc && desc.descuento_pct > 0) {
                        return (
                          <span className="cb-item-precio cb-item-precio--prof">
                            <span className="cb-precio-publico">{formatEur(desc.precio_publico, lang)}</span>
                            <span className="cb-precio-final">{formatEur(desc.precio_profesor, lang)}</span>
                            <span className="cb-precio-pct">−{desc.descuento_pct}%</span>
                          </span>
                        );
                      }
                      return <span className="cb-item-precio">{formatEur(b.precio_euros, lang)}</span>;
                    })()}
                    <button
                      type="button"
                      className="btn-primary cb-item-btn"
                      onClick={() => comprar(b)}
                      disabled={!!enviando}
                    >
                      {inFlight ? (
                        <><Loader2 size={14} className="spin" strokeWidth={1.5} /> {t('comprar_bono.btn_redirigiendo')}</>
                      ) : (
                        t('comprar_bono.btn_comprar')
                      )}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {errorMsg && (
          <div className="modal-error-general">
            <AlertCircle size={16} strokeWidth={1.75} />
            <span>{errorMsg}</span>
          </div>
        )}

        <p className="cb-pie">{t('comprar_bono.pie_seguridad')}</p>
      </div>
    </div>,
    document.body
  );
}

export default function MisBonos() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [datos, setDatos] = useState({ data: null, error: null, loading: true });
  const [modalCompra, setModalCompra] = useState(false);
  const [bannerDismissTick, setBannerDismissTick] = useState(0);
  // SS7.2.5: si soy profesor, cargar descuentos bono para mostrar precios reducidos.
  const [descuentosBono, setDescuentosBono] = useState(null);  // Map bono_tipo_id → {pct, precio_publico, precio_profesor}

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || user?.rol !== 'profesor') {
      setDescuentosBono(null);
      return;
    }
    let cancelled = false;
    const token = readToken();
    if (!token) return;
    getProfesorPrecios(token)
      .then((res) => {
        if (cancelled) return;
        const map = new Map();
        for (const b of res.bonos || []) {
          if (b.descuento_pct > 0) map.set(b.bono_tipo_id, b);
        }
        setDescuentosBono(map);
      })
      .catch(() => { /* silencio */ });
    return () => { cancelled = true; };
  }, [authLoading, isAuthenticated, user?.rol]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setDatos({ data: null, error: null, loading: false });
      return;
    }
    let cancelled = false;
    setDatos({ data: null, error: null, loading: true });
    const token = readToken();
    getMisBonos(token, lang)
      .then((json) => {
        if (cancelled) return;
        setDatos({ data: json, error: null, loading: false });
      })
      .catch((err) => {
        if (cancelled) return;
        setDatos({ data: null, error: err.message || 'error', loading: false });
      });
    return () => { cancelled = true; };
  }, [authLoading, isAuthenticated, lang]);

  if (authLoading) {
    return (
      <main className="mis-bonos-page">
        <div className="mr-vacio"><Loader2 size={20} className="spin" strokeWidth={1.5} /></div>
      </main>
    );
  }
  if (!isAuthenticated) return <Navigate to="/horarios" replace />;

  if (datos.loading) {
    return (
      <main className="mis-bonos-page">
        <header className="mr-head">
          <span className="section-eyebrow">{t('auth.mis_bonos.eyebrow')}</span>
          <h1 className="mr-titulo">{t('auth.mis_bonos.titulo')}</h1>
        </header>
        <div className="mr-vacio"><Loader2 size={20} className="spin" strokeWidth={1.5} /></div>
      </main>
    );
  }
  if (datos.error) {
    return (
      <main className="mis-bonos-page">
        <header className="mr-head">
          <span className="section-eyebrow">{t('auth.mis_bonos.eyebrow')}</span>
          <h1 className="mr-titulo">{t('auth.mis_bonos.titulo')}</h1>
        </header>
        <div className="mr-vacio">{t('auth.mis_bonos.error')}</div>
      </main>
    );
  }

  const activos = datos.data?.activos || [];
  const pasados = (datos.data?.bonos || []).filter((b) => !b.vigente);
  const historial = datos.data?.historial || [];
  const total = activos.length + pasados.length;

  const caducando = activos.filter((b) => {
    if (b.es_membresia) return false;
    if (!b.fecha_caducidad) return false;
    if (b.creditos_restantes <= 0) return false;
    const d = diasHasta(b.fecha_caducidad);
    return d !== null && d >= 0 && d <= 7;
  });
  const dismissKey = caducando.length
    ? 'idy_banner_caduca_dismissed_' + caducando.map((b) => b.id).sort().join(',')
    : null;
  const bannerDismissed = dismissKey ? sessionStorage.getItem(dismissKey) === '1' : true;
  const dismissBanner = () => {
    if (dismissKey) sessionStorage.setItem(dismissKey, '1');
    setBannerDismissTick((n) => n + 1);
  };
  // Reference tick so React re-reads sessionStorage on dismiss.
  void bannerDismissTick;

  return (
    <main className="mis-bonos-page">
      <header className="mr-head mb-head">
        <div>
          <span className="section-eyebrow">{t('auth.mis_bonos.eyebrow')}</span>
          <h1 className="mr-titulo">{t('auth.mis_bonos.titulo')}</h1>
          <p className="mr-sub">{t('auth.mis_bonos.sub', { nombre: user?.nombre || '' })}</p>
        </div>
        <button
          type="button"
          className="btn-primary mb-cta-comprar"
          onClick={() => setModalCompra(true)}
        >
          <Plus size={16} strokeWidth={1.75} />
          <span>{t('auth.mis_bonos.btn_comprar')}</span>
        </button>
      </header>

      {total === 0 && (
        <div className="mr-vacio">
          {t('auth.mis_bonos.vacio')}{' '}
          <button type="button" className="mb-vacio-link" onClick={() => setModalCompra(true)}>
            {t('auth.mis_bonos.vacio_cta')}
          </button>
        </div>
      )}

      {modalCompra && <ModalComprarBono onClose={() => setModalCompra(false)} lang={lang} descuentosBono={descuentosBono} />}

      {caducando.length > 0 && !bannerDismissed && (
        <aside className="mb-banner-caduca" role="status">
          <Clock size={18} strokeWidth={1.75} className="mb-banner-icon" />
          <div className="mb-banner-info">
            <strong className="mb-banner-titulo">
              {caducando.length === 1
                ? t('auth.mis_bonos.banner_caduca.titulo_one', {
                    nombre: caducando[0].nombre,
                    dias: diasHasta(caducando[0].fecha_caducidad),
                  })
                : t('auth.mis_bonos.banner_caduca.titulo_other', { count: caducando.length })}
            </strong>
            <span className="mb-banner-sub">{t('auth.mis_bonos.banner_caduca.sub')}</span>
          </div>
          <button
            type="button"
            className="mb-banner-cta"
            onClick={() => setModalCompra(true)}
          >
            {t('auth.mis_bonos.banner_caduca.cta')}
          </button>
          <button
            type="button"
            className="mb-banner-cerrar"
            onClick={dismissBanner}
            aria-label={t('auth.mis_bonos.banner_caduca.cerrar')}
          >
            <X size={16} strokeWidth={1.75} />
          </button>
        </aside>
      )}

      {activos.length > 0 && (
        <section className="mr-grupo">
          <h2 className="mr-grupo-titulo">{t('auth.mis_bonos.activos')}</h2>
          <div className="mb-cards">
            {activos.map((b) => <CardBono key={b.id} bono={b} lang={lang} />)}
          </div>
        </section>
      )}

      {pasados.length > 0 && (
        <section className="mr-grupo">
          <h2 className="mr-grupo-titulo">{t('auth.mis_bonos.pasados')}</h2>
          <div className="mb-cards">
            {pasados.map((b) => <CardBono key={b.id} bono={b} lang={lang} />)}
          </div>
        </section>
      )}

      {historial.length > 0 && (
        <section className="mr-grupo">
          <h2 className="mr-grupo-titulo">{t('auth.mis_bonos.historial')}</h2>
          <ul className="mb-historial">
            {historial.map((h) => <ItemHistorial key={h.id} item={h} lang={lang} />)}
          </ul>
        </section>
      )}
    </main>
  );
}
